import type { ReactNode } from 'react';
import {
  PIPELINE_FLAGS,
  PIPELINE_FLAG_LABELS,
  type PipelineFilter,
  type TagOption,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { DatePicker } from '../../../components/DatePicker';
import { Field, FieldRow } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { MultiSelect, Select } from '../../../components/Select';
import { listOf } from '../pipeline-search';
import { useSourceOptions } from '../../../lib/app-settings';
import styles from './PipelineToolbar.module.css';

const NO_RECRUITER = 'none';
const ANY = '';

interface PipelineFiltersProps {
  readonly filter: PipelineFilter;
  readonly owners: readonly { readonly id: string; readonly fullName: string }[];
  readonly tags: readonly TagOption[];
  readonly onChange: (key: keyof PipelineFilter, value: string | undefined) => void;
  readonly onClear: () => void;
}

/** SCR-02's filters (E6 "sort/filter by score, source, date, knockout results"), each one in the address. */
export function PipelineFilters({ filter, owners, tags, onChange, onClear }: PipelineFiltersProps): ReactNode {
  const sources = useSourceOptions('filter');
  const flags = listOf(filter.flag);

  return (
    <div className={styles.filters}>
      <FieldRow>
        <Field label="Match from">
          {({ inputId }) => <Input id={inputId} isNumeric inputMode="numeric" placeholder="0" value={filter.scoreMin ?? ''} onChange={(event) => onChange('scoreMin', digits(event.target.value))} />}
        </Field>
        <Field label="to">
          {({ inputId }) => <Input id={inputId} isNumeric inputMode="numeric" placeholder="100" value={filter.scoreMax ?? ''} onChange={(event) => onChange('scoreMax', digits(event.target.value))} />}
        </Field>
      </FieldRow>
      <Field label="Source">
        {({ inputId }) => <MultiSelect id={inputId} label="Source" placeholder="Any source" options={sources} value={listOf(filter.source)} onChange={(value) => onChange('source', value.join(','))} />}
      </Field>
      <Field label="Recruiter">
        {({ inputId }) => (
          <Select
            id={inputId}
            label="Recruiter"
            value={filter.recruiter ?? ANY}
            options={[{ value: ANY, label: 'Anyone' }, { value: NO_RECRUITER, label: 'Nobody yet' }, ...owners.map((owner) => ({ value: owner.id, label: owner.fullName }))]}
            onChange={(value) => onChange('recruiter', value)}
          />
        )}
      </Field>
      {tags.length > 0 && (
        <Field label="Tags">
          {({ inputId }) => <MultiSelect id={inputId} label="Tags" placeholder="Any tag" options={tags.map((tag) => ({ value: tag.id, label: tag.name }))} value={listOf(filter.tag)} onChange={(value) => onChange('tag', value.join(','))} />}
        </Field>
      )}
      <FieldRow>
        <Field label="Applied from">{({ inputId }) => <DatePicker id={inputId} label="Applied from" value={filter.appliedFrom ?? ''} onChange={(value) => onChange('appliedFrom', value)} />}</Field>
        <Field label="to">{({ inputId }) => <DatePicker id={inputId} label="Applied to" value={filter.appliedTo ?? ''} onChange={(value) => onChange('appliedTo', value)} />}</Field>
      </FieldRow>
      <fieldset className={styles.flags}>
        <legend className={styles.legend}>Only people who are</legend>
        {PIPELINE_FLAGS.map((flag) => (
          <Checkbox
            key={flag}
            checked={flags.includes(flag)}
            label={PIPELINE_FLAG_LABELS[flag]}
            onChange={(checked) => onChange('flag', (checked ? [...flags, flag] : flags.filter((other) => other !== flag)).join(','))}
          />
        ))}
      </fieldset>
      <div className={styles.filterFooter}>
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear all filters
        </Button>
      </div>
    </div>
  );
}

function digits(value: string): string | undefined {
  const cleaned = value.replace(/\D/g, '').slice(0, 3);
  return cleaned === '' ? undefined : cleaned;
}
